import React, { useState } from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/EditOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import AddIcon from '@material-ui/icons/Add';
import { Box, Flex } from '@rebass/grid';
import { useMutation, useQueryClient } from 'react-query';
import { useUserAccomplishments } from '../../lib/hooks/use-user-accomplishments';
import {
  createAccomplishment,
  updateAccomplishment,
  deleteAccomplishment,
} from '../../lib/mutations/accomplishments';

const TYPES = ['Tournament', 'Seminar', 'Certification', 'Other'];
const PLACEMENTS = ['1st', '2nd', '3rd'];
const MEDALS = { '1st': '🥇', '2nd': '🥈', '3rd': '🥉' };
const emptyAccomplishment = {
  accomplishment_type: 'Tournament',
  name: '',
  placement: '1st',
  description: '',
};

const AccomplishmentsCard = ({ userUid, style }) => {
  const { accomplishments, isLoading } = useUserAccomplishments(userUid);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [form, setForm] = useState(emptyAccomplishment);
  const [error, setError] = useState('');

  const refresh = () => {
    queryClient.invalidateQueries(['user-accomplishments', userUid]);
    queryClient.invalidateQueries(['user', userUid]);
  };

  const saveMutation = useMutation(
    () =>
      editing?.id
        ? updateAccomplishment(userUid, editing.id, form)
        : createAccomplishment(userUid, form),
    {
      onSuccess: () => {
        refresh();
        setEditing(null);
        setForm(emptyAccomplishment);
        setError('');
      },
      onError: (mutationError) => setError(mutationError.message),
    },
  );

  const deleteMutation = useMutation(
    (accomplishmentId) => deleteAccomplishment(userUid, accomplishmentId),
    {
      onSuccess: () => {
        refresh();
        setPendingDelete(null);
      },
    },
  );

  const openCreate = () => {
    setEditing({});
    setForm(emptyAccomplishment);
    setError('');
  };

  const openEdit = (accomplishment) => {
    setIsDetailsOpen(false);
    setEditing(accomplishment);
    setForm({
      accomplishment_type: accomplishment.accomplishment_type,
      name: accomplishment.name,
      placement: accomplishment.placement,
      description: accomplishment.description || '',
    });
    setError('');
  };

  const closeDialog = () => {
    if (!saveMutation.isLoading) {
      setEditing(null);
      setError('');
    }
  };

  return (
    <Card style={style}>
      <CardContent>
        <Flex justifyContent="space-between" alignItems="center" mb="8px">
          <Typography variant="h6">Accomplishments</Typography>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            Add
          </Button>
        </Flex>

        {isLoading ? (
          <Typography>Loading accomplishments...</Typography>
        ) : accomplishments.length === 0 ? (
          <Box py="8px">
            <Typography color="textSecondary">No accomplishments yet</Typography>
          </Box>
        ) : (
          <>
            <Flex
              flexWrap="wrap"
              alignItems="center"
              style={{ gap: '8px', minHeight: '48px' }}
            >
              {accomplishments.map((accomplishment) => (
                <Box
                  key={accomplishment.id}
                  title={`${accomplishment.placement} · ${accomplishment.name}`}
                  aria-label={`${accomplishment.placement} medal for ${accomplishment.name}`}
                  style={{ fontSize: '32px', lineHeight: 1 }}
                >
                  {MEDALS[accomplishment.placement] || '🏅'}
                </Box>
              ))}
            </Flex>
            <Button
              color="primary"
              size="small"
              onClick={() => setIsDetailsOpen(true)}
              style={{ marginTop: '12px' }}
            >
              View accomplishments ({accomplishments.length})
            </Button>
          </>
        )}
      </CardContent>

      <Dialog
        open={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Accomplishments</DialogTitle>
        <DialogContent dividers>
          <List disablePadding>
            {accomplishments.map((accomplishment) => (
              <ListItem key={accomplishment.id} divider>
                <Box mr="12px" style={{ fontSize: '28px', lineHeight: 1 }}>
                  {MEDALS[accomplishment.placement] || '🏅'}
                </Box>
                <ListItemText
                  primary={`${accomplishment.placement} · ${accomplishment.name}`}
                  secondary={
                    <>
                      <span>{accomplishment.accomplishment_type}</span>
                      {accomplishment.description && (
                        <span>{` — ${accomplishment.description}`}</span>
                      )}
                    </>
                  }
                />
                <IconButton
                  aria-label={`Edit ${accomplishment.name}`}
                  size="small"
                  onClick={() => openEdit(accomplishment)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label={`Delete ${accomplishment.name}`}
                  size="small"
                  onClick={() => setPendingDelete(accomplishment)}
                  disabled={deleteMutation.isLoading}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!pendingDelete}
        onClose={() => !deleteMutation.isLoading && setPendingDelete(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete accomplishment?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete {pendingDelete?.placement} place for {pendingDelete?.name}?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingDelete(null)}
            disabled={deleteMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            color="secondary"
            onClick={() => deleteMutation.mutate(pendingDelete.id)}
            disabled={deleteMutation.isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editing} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id ? 'Edit accomplishment' : 'Add accomplishment'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel id="accomplishment-type-label">Type</InputLabel>
            <Select
              labelId="accomplishment-type-label"
              value={form.accomplishment_type}
              onChange={(event) =>
                setForm({ ...form, accomplishment_type: event.target.value })
              }
            >
              {TYPES.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            required
            margin="normal"
            label="Tournament name"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="accomplishment-placement-label">Accomplishment</InputLabel>
            <Select
              labelId="accomplishment-placement-label"
              value={form.placement}
              onChange={(event) => setForm({ ...form, placement: event.target.value })}
            >
              {PLACEMENTS.map((placement) => (
                <MenuItem key={placement} value={placement}>{placement}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            margin="normal"
            label="Description"
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />
          {error && <Typography color="error">{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            color="primary"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isLoading || !form.name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AccomplishmentsCard;
